/**
 * Barcode Lookup Service
 * 
 * Provides methods to lookup album metadata from barcodes (UPC/EAN)
 * Uses MusicBrainz API via Firebase Functions
 */

import { API_BASE, getAuthHeaders } from '../../config/api';

class BarcodeService {
  /**
   * Lookup album metadata by barcode
   * @param {string} barcode - UPC/EAN barcode (numeric string)
   * @returns {Promise<Object>} Album metadata from MusicBrainz
   */
  async lookupBarcode(barcode) {
    if (!barcode || !/^\d+$/.test(barcode)) {
      throw new Error('Invalid barcode. Must be numeric (UPC/EAN format)');
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/barcode-lookup/${barcode}`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Barcode lookup error:', error);
      throw error;
    }
  }

  /**
   * Get the best match from barcode lookup results
   * Returns the first release, or allows filtering by preferences
   * @param {Object} lookupResult - Result from lookupBarcode
   * @returns {Object|null} Best matching release
   */
  getBestMatch(lookupResult) {
    if (!lookupResult.success || !lookupResult.releases || lookupResult.releases.length === 0) {
      return null;
    }

    // Return the first release (usually the best match from MusicBrainz)
    // Could add logic here to prefer certain countries, dates, etc.
    return lookupResult.releases[0];
  }
}

// Export singleton instance
export const barcodeService = new BarcodeService();
export default barcodeService;

